import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';
import {
  FiUsers, FiDollarSign, FiClock, FiAward, FiTrendingUp,
  FiShield, FiSmartphone, FiCheckCircle, FiChevronDown,
  FiBarChart2, FiCalendar, FiBriefcase, FiTarget
} from 'react-icons/fi';
import CorehrtaskImage from '../Assets/Images/Corehr-task-Image.png';
import MeetingImg from '../Assets/Images/Corehr-Meeting-Image.png'
import TeamsImg from '../Assets/Images/Corehr-teams-Image.png';
import RoleArchitectureImg from '../Assets/Images/Corehr-RoleArchitecture-Image.png';

/* ── Animated Counter Widget ── */
const KpiWidget = ({ icon, value, suffix, label, color }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  return (
    <div ref={ref} className={`bg-white rounded-2xl p-5 shadow-md border-l-4 ${color} flex items-center gap-4`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-50 text-indigo-600 text-2xl`}>{icon}</div>
      <div>
        <p className="text-2xl font-medium text-gray-900">
          {inView ? <CountUp end={value} duration={2.2} suffix={suffix} separator="," /> : '0'}
        </p>
        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mt-0.5">{label}</p>
      </div>
    </div>
  );
};

/* ── FAQ Accordion ── */
const Faq = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-indigo-50 transition-colors">
            <span className="font-medium text-gray-900">{f.q}</span>
            <FiChevronDown className={`transition-transform duration-300 text-indigo-500 ${open === i ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <p className="p-5 pt-2 text-gray-600 font-medium border-t border-gray-100">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* ── App Gallery ── */
const AppGallery = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = [
    { name: 'Task Sheet', img: CorehrtaskImage },
    { name: 'Teams', img: TeamsImg },
    { name: 'Meetings', img: MeetingImg },
    { name: 'Role Architecture', img: RoleArchitectureImg },
  ];

  return (
    <div className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-4xl font-medium text-gray-900 mb-3">See Core HR 360 in Action</h2>
          <p className="text-gray-500 text-lg font-medium max-w-xl mx-auto">Take a glimpse at the intuitive and powerful interface driving modern HR teams.</p>
        </div>
        
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Tabs Column (Col-2) */}
          <div className="w-full md:w-2/12 flex flex-col gap-3">
            {tabs.map((tab, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-all ${activeTab === i ? 'bg-indigo-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          {/* Image Column (Col-10) */}
          <div className="w-full md:w-10/12 rounded-2xl overflow-hidden shadow-2xl border border-gray-100 bg-gray-50">
            <AnimatePresence mode="wait">
              <motion.img
                key={activeTab}
                src={tabs[activeTab].img}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="w-full h-auto object-contain block"
                alt={tabs[activeTab].name}
              />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
export default function CoreHR360() {
  useEffect(() => window.scrollTo(0, 0), []);
  const [ref, inView] = useInView({ triggerOnce: true });

  const steps = [
    { icon: <FiBriefcase />, title: 'Recruit', desc: 'ATS & job postings' },
    { icon: <FiUsers />, title: 'Onboard', desc: 'Digital checklists & documents' },
    { icon: <FiClock />, title: 'Attend', desc: 'Biometric & geo-tracking' },
    { icon: <FiDollarSign />, title: 'Payroll', desc: 'Auto-process & compliance' },
    { icon: <FiTarget />, title: 'Evaluate', desc: '360° performance reviews' },
  ];

  const bentoFeatures = [
    { icon: <FiDollarSign />, title: 'Payroll Automation', desc: 'One-click salary disbursement with PF/ESI/TDS built-in. Process 10,000+ employees in minutes.', span: 'md:col-span-2 md:row-span-2', bg: 'bg-indigo-600 text-white', img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=500&q=80' },
    { icon: <FiClock />, title: 'Smart Attendance', desc: 'Geo-fenced check-ins and biometric sync.', span: '', bg: 'bg-indigo-50', img: null },
    { icon: <FiAward />, title: 'Performance OKRs', desc: '360° reviews and goal alignment.', span: '', bg: 'bg-purple-50', img: null },
    { icon: <FiShield />, title: 'Compliance Guard', desc: 'Always up-to-date with labour law changes.', span: '', bg: 'bg-green-50', img: null },
    { icon: <FiSmartphone />, title: 'Mobile ESS App', desc: 'Payslips, leaves, claims from any device.', span: 'md:col-span-2', bg: 'bg-slate-900 text-white', img: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&q=80' },
  ];

  const faqs = [
    { q: 'How quickly can we go live?', a: 'Most organisations are fully live within 3–4 weeks, including data migration from legacy systems.' },
    { q: 'Is payroll compliant with Indian tax law?', a: 'Yes — PF, ESI, PT, TDS, LWF are all calculated automatically and updated with every budget cycle.' },
    { q: 'Can we integrate biometric devices?', a: 'Yes. We support 90%+ of market biometric brands via direct DB sync or API.' },
    { q: 'Is there a mobile app for employees?', a: 'Yes, native iOS and Android apps for leave requests, payslip downloads, and claims.' },
  ];

  return (
    <div className="min-h-screen bg-[#f5f6fa] font-sans">
      <div className="bg-[#0f1c3f] text-white"><Header /></div>

      {/* ── HERO: Dashboard Split ── */}
      <section className="bg-gradient-to-br from-[#0f1c3f] via-[#1a2f6e] to-[#263a8a] text-white pt-32 pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-end gap-0">
          {/* Left */}
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="w-full lg:w-1/2 pb-16 pr-0 lg:pr-10">
            <span className="inline-block px-3 py-1 bg-indigo-500/20 text-indigo-300 text-xs font-medium rounded-full border border-indigo-500/30 mb-6 uppercase tracking-widest">HR Management Suite</span>
            <h1 className="text-5xl lg:text-6xl font-medium leading-tight mb-6">
              The HR platform your<br /><span className="text-indigo-300 font-semibold">people deserve.</span>
            </h1>
            <p className="text-indigo-100 text-lg mb-10 max-w-md font-light leading-relaxed">Automate payroll, track attendance in real-time, and run performance cycles — all from one intelligent HRMS.</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products#DemoForm">
                <button className="bg-white text-indigo-700 font-medium px-8 py-4 rounded-xl hover:bg-indigo-50 transition-all shadow-lg">Request Demo</button>
              </Link>

              <Link to="https://corehr360.vivekasoftek.com/" target="_blank" rel="noopener noreferrer">
                <button className="bg-indigo-500/20 border border-indigo-400/40 text-white font-semibold px-8 py-4 rounded-xl hover:bg-indigo-500/30 transition-all">Get Started Free</button>
              </Link>
            </div>
          </motion.div>

          {/* Right — Dashboard Panel */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="w-full lg:w-1/2">
            <div className="bg-[#1e2d5a]/80 backdrop-blur border border-indigo-500/20 rounded-t-3xl p-6 shadow-2xl">
              {/* Fake browser bar */}
              <div className="flex items-center gap-2 mb-5">
                <span className="w-3 h-3 rounded-full bg-red-400"></span>
                <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                <span className="w-3 h-3 rounded-full bg-green-400"></span>
                <div className="flex-1 ml-3 h-6 bg-white/10 rounded-md flex items-center px-3"><span className="text-xs text-indigo-300">core-hr360.app/dashboard</span></div>
              </div>
              {/* Dashboard KPI grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: 'Active Employees', value: '1,248', delta: '+12', color: 'text-green-400', iconColor: 'text-sky-400', icon: <FiUsers /> },
                  { label: 'Payroll Processed', value: '₹24.6L', delta: 'This month', color: 'text-indigo-300', iconColor: 'text-emerald-400', icon: <FiDollarSign /> },
                  { label: 'Attendance Rate', value: '96.3%', delta: '+1.2%', color: 'text-green-400', iconColor: 'text-green-400', icon: <FiCheckCircle /> },
                  { label: 'Open Leaves', value: '47', delta: '3 pending', color: 'text-amber-400', iconColor: 'text-amber-400', icon: <FiCalendar /> },
                ].map((k, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className={`text-2xl mb-2 ${k.iconColor}`}>{k.icon}</div>
                    <p className="text-white font-medium text-lg">{k.value}</p>
                    <p className="text-indigo-200 text-xs font-light">{k.label}</p>
                    <p className={`text-xs font-semibold mt-1 ${k.color}`}>{k.delta}</p>
                  </div>
                ))}
              </div>
              {/* Mini bar chart */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-xs text-indigo-300 font-semibold mb-3 uppercase tracking-widest">Attendance — Last 7 Days</p>
                <div className="flex items-end gap-1.5 h-12">
                  {[88, 94, 91, 96, 93, 97, 95].map((v, i) => (
                    <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${v}%` }} transition={{ duration: 0.6, delay: i * 0.08 }}
                      className="flex-1 bg-indigo-400 rounded-sm opacity-80" style={{ minHeight: '4px' }}></motion.div>
                  ))}
                </div>
                <div className="flex justify-between mt-1">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(d => <span key={d} className="text-[10px] text-indigo-400">{d}</span>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STAT COUNTERS ── */}
      <section ref={ref} className="bg-white py-14 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: <FiUsers />, value: 500, suffix: '+', label: 'Enterprise Clients', color: 'border-indigo-500' },
            { icon: <FiUsers />, value: 2.5, suffix: 'M+', label: 'Employees Managed', color: 'border-purple-500' },
            { icon: <FiDollarSign />, value: 98, suffix: '%', label: 'Payroll Accuracy', color: 'border-green-500' },
            { icon: <FiTrendingUp />, value: 60, suffix: '%', label: 'Time Saved on HR', color: 'border-amber-500' },
          ].map((s, i) => <KpiWidget key={i} {...s} />)}
        </div>
      </section>

      {/* ── APP GALLERY ── */}
      <AppGallery />

      {/* ── HR WORKFLOW STEPPER ── */}
      <section className="py-20 bg-[#f5f6fa]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-medium text-gray-900 mb-3">Your full HR lifecycle, automated</h2>
            <p className="text-gray-500 text-lg font-medium max-w-xl mx-auto">From the first interview to the final appraisal — Core HR 360 handles every step.</p>
          </div>
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-0">
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                  className="flex-1 flex flex-col items-center text-center px-4">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-2xl shadow-lg shadow-indigo-200 mb-4">{s.icon}</div>
                  <span className="text-xs text-indigo-500 font-medium uppercase tracking-widest mb-1">Step {i + 1}</span>
                  <h4 className="text-lg font-medium text-gray-900 mb-1">{s.title}</h4>
                  <p className="text-sm text-gray-500 font-medium">{s.desc}</p>
                </motion.div>
                {i < steps.length - 1 && (
                  <div className="hidden md:block h-px flex-1 border-t-2 border-dashed border-indigo-200 mx-2"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      

      {/* ── BENTO FEATURE GRID ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-medium text-gray-900 mb-3">Every HR tool you'll ever need</h2>
            <p className="text-gray-500 text-lg font-medium">Purpose-built modules that actually talk to each other.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {bentoFeatures.map((f, i) => (
              <motion.div key={i} whileHover={{ scale: 1.02 }} transition={{ type: 'spring', stiffness: 300 }}
                className={`${f.span} rounded-3xl p-7 overflow-hidden relative ${f.bg} shadow-sm border border-gray-100`}>
                <div className={`text-3xl mb-4 ${f.bg.includes('600') || f.bg.includes('900') ? 'text-white/80' : 'text-indigo-600'}`}>{f.icon}</div>
                <h3 className={`text-xl font-medium mb-2 ${f.bg.includes('600') || f.bg.includes('900') ? 'text-white' : 'text-gray-900'}`}>{f.title}</h3>
                <p className={`text-sm leading-relaxed font-medium ${f.bg.includes('600') || f.bg.includes('900') ? 'text-white/70' : 'text-gray-600'}`}>{f.desc}</p>
                {f.img && <img src={f.img} alt={f.title} className="mt-4 rounded-xl w-full h-36 object-cover opacity-80" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PHOTO BANNER ── */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920&q=80" alt="Team" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-indigo-900/70"></div>
        <div className="relative z-10 text-center px-6">
          <h2 className="text-4xl font-medium text-white mb-4">Ready to transform HR at your organization?</h2>
          <Link to="https://corehr360.vivekasoftek.com/" target="_blank" rel="noopener noreferrer">
            <button className="bg-white text-indigo-700 font-medium px-10 py-4 rounded-full text-lg hover:bg-indigo-50 transition-all shadow-xl">Get Started Free</button>
          </Link>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-[#f5f6fa]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-medium text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
          <Faq faqs={faqs} />
        </div>
      </section>

      <div className="bg-[#0f1c3f] text-white"><Footer /></div>
    </div>
  );
}
